import { useApp } from '../context/AppContext';

const STATUS_STYLE = {
  active:      { label: 'Active',      cls: 's-ac' },
  overdue:     { label: 'OVERDUE',     cls: 's-ov' },
  available:   { label: 'Available',   cls: 's-av' },
  booked:      { label: 'Booked',      cls: 's-bk' },
  maintenance: { label: '🔧 In Service', cls: 's-mt' },
};

export default function DetailPanel() {
  const { detailPanel, setDetailPanel, vehicles, rentals, setRentals, setRentalHistory, setRentalDraft, openModal, showToast, areas, locationLog, resolveVehicle, getDamagesForVehicle } = useApp();
  const { open, vid } = detailPanel;

  if (!open || !vid) return null;

  const rawVehicle = vehicles.find(v => v.id === vid);
  // Resolve model-inherited fields (priceDay, deposit, features, etc.)
  const vehicle = (rawVehicle && resolveVehicle) ? resolveVehicle(rawVehicle) : rawVehicle;
  const rental  = rentals.find(r => r.vid === vid && ['active','overdue','booked','complete'].includes(r.status));

  if (!vehicle) return null;

  const st = STATUS_STYLE[vehicle.status] || STATUS_STYLE.available;

  function startRental() {
    setRentalDraft(rental);
    openModal('check-in');
    setDetailPanel({ open: false, vid: null });
  }

  function cancelRental() {
    if (!window.confirm(`Cancel booking for ${rental.customer}? This cannot be undone.`)) return;
    setRentals(prev => prev.filter(r => r.id !== rental.id));
    setRentalHistory(prev => [{
      ...rental,
      status: 'cancelled',
      total: rental.agreedPrice,
      condRet: null, condRetNote: 'Booking cancelled.',
    }, ...prev]);
    showToast('Booking cancelled');
    setDetailPanel({ open: false, vid: null });
  }

  function Row({ label, value, mono, style }) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 12, padding: '6px 0', borderBottom: '1px solid var(--bd)' }}>
        <span style={{ color: 'var(--tx3)', flexShrink: 0, marginRight: 10 }}>{label}</span>
        <span style={{ fontWeight: 500, textAlign: 'right', fontFamily: mono ? "'DM Mono',monospace" : undefined, ...style }}>{value || '—'}</span>
      </div>
    );
  }

  function Sect({ title }) {
    return (
      <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', padding: '10px 0 4px', marginTop: 4 }}>
        {title}
      </div>
    );
  }

  return (
    <div className="dp open" style={{
      position: 'fixed', right: 0, top: 0, bottom: 0, width: 320,
      background: 'var(--sur)', borderLeft: '1px solid var(--bd)',
      display: 'flex', flexDirection: 'column', zIndex: 200, boxShadow: 'var(--sh2)',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--bd)', background: 'var(--sur2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{vehicle.icon} {vehicle.name.split('–')[0].trim()}</div>
            <div style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace", marginTop: 2 }}>
              {vehicle.type.toUpperCase()} · {vehicle.plate}
            </div>
          </div>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--tx3)', padding: '0 4px' }}
            onClick={() => setDetailPanel({ open: false, vid: null })}>×</button>
        </div>
        <div style={{ marginTop: 8 }}>
          <span className={`sp ${st.cls}`}>{st.label}</span>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
        <Sect title="Vehicle Info" />
        <Row label="Seats/Capacity" value={vehicle.seats} />
        <Row label="Features" value={vehicle.features} />
        <Row label="Daily rate" value={`€${vehicle.priceDay}`} mono />
        <Row label="Deposit" value={`€${vehicle.deposit}`} mono />
        <Row label="Confirmation" value={vehicle.confirm === 'auto' ? '⚡ Auto' : '👤 Manual'} />
        {(() => {
          const area = areas.find(a => a.id === vehicle.currentAreaId);
          return area ? <Row label="Current area" value={`${area.icon} ${area.name}`} /> : null;
        })()}

        {/* Damage History */}
        {(() => {
          const vehDamages = getDamagesForVehicle ? getDamagesForVehicle(vid) : [];
          const unrepairedCount = vehDamages.filter(d => !d.repaired && d.status !== 'resolved').length;
          const openPaymentCount = vehDamages.filter(d => ['reported', 'charging', 'disputed', 'insurance'].includes(d.status)).length;
          const headline = `Damage History · ${vehDamages.length}`
                         + (unrepairedCount ? ` · 🔧 ${unrepairedCount} on car` : '')
                         + (openPaymentCount ? ` · 💰 ${openPaymentCount} open payment` : '');
          return (
            <>
              <Sect title={headline} />
              {vehDamages.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--tx3)', fontStyle: 'italic', padding: '4px 0 8px' }}>
                  ✅ No damages recorded.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {vehDamages.slice(0, 8).map((d, i) => {
                    const cost = d.finalCost ?? d.estimatedCost ?? 0;
                    const sevColor = d.severity === 'major' ? '#ef4444'
                                  : d.severity === 'moderate' ? '#f59e0b'
                                  : '#10b981';
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => openModal('damage', d.id)}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0',
                          background: 'none', border: 'none',
                          borderBottom: i < Math.min(vehDamages.length, 8) - 1 ? '1px solid var(--bd)' : 'none',
                          cursor: 'pointer', textAlign: 'left', width: '100%',
                        }}
                      >
                        <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: sevColor, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {d.location || 'Damage'}
                            </span>
                            <span style={{ fontFamily: "'DM Mono',monospace", color: 'var(--tx2)' }}>€{Number(cost).toFixed(0)}</span>
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {d.reportedDate ? new Date(d.reportedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                            {d.status ? ' · ' + d.status : ''}
                            {d.customerName ? ' · ' + d.customerName : ''}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {vehDamages.length > 8 && (
                    <div style={{ fontSize: 10, color: 'var(--tx3)', textAlign: 'center', padding: '6px 0' }}>
                      + {vehDamages.length - 8} more
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}

        {/* Location History */}
        {(() => {
          const vehLog = (locationLog || []).filter(e => e.vid === vid);
          if (vehLog.length === 0) return null;
          return (
            <>
              <Sect title="Location History" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {vehLog.slice(0, 10).map((entry, i) => {
                  const from = areas.find(a => a.id === entry.fromAreaId);
                  const to = areas.find(a => a.id === entry.toAreaId);
                  const date = new Date(entry.timestamp);
                  const fmtDate = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                  const fmtTime = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                  const isReturn = entry.reason === 'rental_return';
                  return (
                    <div key={entry.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0',
                      borderBottom: i < vehLog.length - 1 ? '1px solid var(--bd)' : 'none',
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                        background: isReturn ? 'var(--bl-l)' : 'var(--am-l)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                      }}>
                        {isReturn ? '🚗' : '🔄'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 600 }}>
                          {from?.name || '?'} → {to?.name || '?'}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>
                          {isReturn ? 'Rental return' : 'Manual transfer'}{entry.rentalId ? ` · ${entry.rentalId}` : ''} · {fmtDate} {fmtTime}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {vehLog.length > 10 && (
                <div style={{ fontSize: 10, color: 'var(--tx3)', textAlign: 'center', padding: '6px 0' }}>
                  + {vehLog.length - 10} more entries
                </div>
              )}
            </>
          );
        })()}

        {rental && (
          <>
            <Sect title="Current Rental" />
            <Row label="Contract" value={rental.id} mono />
            <Row label="Customer" value={rental.customer} style={{ fontWeight: 700 }} />
            <Row label="Phone" value={rental.phone} />
            <Row label="Email" value={rental.email} />
            <Row label="Passport / ID" value={rental.passport} mono />
            <Row label="Licence No." value={rental.licence} mono />
            {rental.licExpiry && <Row label="Licence Expiry" value={rental.licExpiry} mono />}
            <Row label="Start" value={rental.startLabel} />
            <Row label="Return" value={rental.endLabel} style={rental.status === 'overdue' ? { color: 'var(--ac)', fontWeight: 700 } : {}} />
            <Sect title="Financial" />
            <Row label="Agreed price" value={`€${rental.agreedPrice}`} mono style={{ color: 'var(--gr)', fontSize: 14, fontWeight: 700 }} />
            <Row label="Deposit" value={`€${rental.deposit}${rental.depositMethod ? ' · ' + rental.depositMethod : ''}`} mono />
            <Row label="Payment" value={rental.payMethod} />
            {rental.notes && (
              <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 6, fontSize: 11, color: 'var(--tx2)' }}>
                💬 {rental.notes}
              </div>
            )}
          </>
        )}

        {rental?.status === 'complete' && (
          <>
            <Sect title="Status" />
            <div style={{ padding: '12px 0', textAlign: 'center', color: 'var(--gr)', fontSize: 12, fontWeight: 500 }}>
              ✓ Rental completed
            </div>
          </>
        )}

        {!rental && (
          <>
            <Sect title="Status" />
            <div style={{ padding: '12px 0', textAlign: 'center', color: 'var(--gr)', fontSize: 12, fontWeight: 500 }}>
              ✅ Vehicle available for rental
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 16px', borderTop: '1px solid var(--bd)', display: 'flex', gap: 6 }}>
        {(rental?.status === 'active' || rental?.status === 'overdue') && (
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', background: 'var(--ac)' }}
            onClick={() => openModal(rental.type === 'lease' ? 'end-lease' : 'end-rental', vid)}>
            {rental.type === 'lease' ? 'End Lease' : 'End Rental'}
          </button>
        )}
        {rental?.status === 'booked' && (
          <>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', background: 'var(--gr)' }}
              onClick={startRental}>
              ✅ Start Rental
            </button>
            <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', color: 'var(--ac)' }}
              onClick={cancelRental}>
              ✕ Cancel
            </button>
          </>
        )}
        {!rental && (
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}
            onClick={() => openModal('add-rental', vid)}>
            + Start Rental
          </button>
        )}
        {rental?.status === 'complete' && (
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}
            onClick={() => openModal('add-rental', vid)}>
            + New Rental
          </button>
        )}
        <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }}
          onClick={() => setDetailPanel({ open: false, vid: null })}>
          Close
        </button>
      </div>
    </div>
  );
}
